import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Award, Sparkle, ArrowUpRight } from "lucide-react";

const cursive = { fontFamily: "'Segoe Script', 'Caveat', cursive" };

// ---------------------------------------------------------------------------
// Story content — kept as data so the visual stays declarative
// ---------------------------------------------------------------------------
const storyBlocks = [
  {
    type: "p",
    text: "Before design became my path, I was a developer. I studied computer engineering and genuinely enjoyed learning how to code.",
  },
  {
    type: "quote",
    text: "The logic, the problem-solving, the quiet satisfaction of making something broken finally work.",
  },
  {
    type: "quote",
    text: "There’s something almost meditative about breaking a problem down to its smallest pieces and building it back up again.",
  },
  {
    type: "p",
    text: "Over time, though, I realised something was missing. While I enjoyed building things, I was increasingly drawn to the experience behind them — how people felt while using technology, where they struggled, and why certain interactions felt intuitive while others didn’t. Coding taught me how things function, but I wanted to focus on how they connect with people.",
  },
  {
    type: "p",
    text: "That curiosity led me to the world of UI/UX design. I began learning through courses while working on real-world projects alongside them. Design, for me, became a way to blend logic with empathy — to solve problems while keeping people at the centre.",
  },
  { type: "tagline", text: "Always curious. Student for life." },
];

const interestChips = ["Mental health is important", "Music & movement"];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const StoryBlock = ({ block }) => {
  if (block.type === "quote") {
    return (
      <blockquote
        style={cursive}
        className="text-[17px] lg:text-[19px] leading-[1.55] text-neutral-900 dark:text-neutral-100 border-l-2 border-neutral-300 dark:border-neutral-700 pl-5"
      >
        {block.text}
      </blockquote>
    );
  }
  if (block.type === "tagline") {
    return (
      <p style={cursive} className="text-[17px] lg:text-[18px] text-neutral-900 dark:text-neutral-50">
        {block.text}
      </p>
    );
  }
  return <p>{block.text}</p>;
};

const ModalHeader = () => (
  <>
    <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-neutral-500 dark:text-neutral-400">
      <Sparkle className="w-3 h-3" strokeWidth={2.25} />
      My journey
    </div>
    <h3 className="mt-4 text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.1] tracking-[-0.025em] font-medium text-neutral-900 dark:text-neutral-50">
      Hey,{" "}
      <span style={cursive} className="font-normal text-neutral-600 dark:text-neutral-400">
        nice to meet you.
      </span>
    </h3>
    <p className="mt-3 text-[15px] text-neutral-500 dark:text-neutral-400">
      Really glad you’re here.
    </p>
  </>
);

const InterestChips = () => (
  <div className="mt-9 flex flex-wrap gap-2.5">
    {interestChips.map((chip) => (
      <span
        key={chip}
        className="inline-flex items-center px-3.5 py-1.5 rounded-full border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 text-[12.5px] text-neutral-700 dark:text-neutral-300"
      >
        {chip}
      </span>
    ))}
  </div>
);

const CertificateCTA = ({ href }) => (
  <div className="mt-10 pt-8 border-t border-neutral-200 dark:border-neutral-800">
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid="journey-modal-certificate-button"
      className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 hover:bg-neutral-700 dark:hover:bg-neutral-200 text-[14.5px] font-medium transition-colors duration-200"
    >
      <Award className="w-4 h-4" strokeWidth={2} />
      View Google UX Certificate
      <ArrowUpRight
        className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        strokeWidth={2.25}
      />
    </a>
  </div>
);

const CloseButton = ({ onClose }) => (
  <button
    type="button"
    onClick={onClose}
    aria-label="Close"
    data-testid="journey-modal-close"
    className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 flex items-center justify-center transition-colors duration-200"
  >
    <X className="w-4 h-4" strokeWidth={2.25} />
  </button>
);

// ---------------------------------------------------------------------------
// Custom hook: lock body scroll + close on Escape
// ---------------------------------------------------------------------------
const useModalShell = (open, onClose) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
};

// ---------------------------------------------------------------------------
// JourneyModal
// ---------------------------------------------------------------------------
const JourneyModal = ({ open, onClose, certificateHref }) => {
  useModalShell(open, onClose);
  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
      data-testid="journey-modal"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-neutral-950/70 backdrop-blur-sm"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-[760px] max-h-[88vh] overflow-y-auto rounded-3xl bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.45)]"
      >
        <CloseButton onClose={onClose} />
        <div className="px-7 pt-10 pb-8 lg:px-12 lg:pt-14 lg:pb-12">
          <ModalHeader />
          <div className="mt-8 space-y-6 text-[15.5px] lg:text-[16px] leading-[1.75] text-neutral-700 dark:text-neutral-300">
            {storyBlocks.map((block, i) => (
              <StoryBlock key={i} block={block} />
            ))}
          </div>
          <InterestChips />
          {certificateHref && <CertificateCTA href={certificateHref} />}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default JourneyModal;
